import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import productData from "../Datas/productData";

const CART_STORAGE_KEY = "cartItems";
const WISHLIST_STORAGE_KEY = "wishlistItems";

const readStoredArray = (key) => {
  if (typeof window === "undefined") return [];

  try {
    const value = JSON.parse(window.localStorage.getItem(key) || "[]");
    return Array.isArray(value) ? value : [];
  } catch {
    return [];
  }
};

const writeStoredArray = (key, value) => {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage can be unavailable in private browsing. The in-memory cart remains usable.
  }
};

const sameId = (left, right) => String(left) === String(right);

const sameCartLine = (item, id, size, color) =>
  sameId(item.id, id) &&
  (item.size || "") === (size || "") &&
  (item.color || "") === (color || "");

const positiveInteger = (value, fallback = 1) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : fallback;
};

const numericPrice = (value) => {
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;

  const parsed = Number(String(value || "").replace(/[^0-9.-]+/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
};

const getProduct = (id) =>
  productData.find((product) => sameId(product.id, id));

const normaliseCartInput = (productOrId, quantityOrOptions, size, color) => {
  const product =
    productOrId && typeof productOrId === "object" ? productOrId : null;
  const options =
    quantityOrOptions && typeof quantityOrOptions === "object"
      ? quantityOrOptions
      : {};

  return {
    id: product?.id ?? productOrId,
    quantity: positiveInteger(
      options.quantity ?? quantityOrOptions ?? product?.quantity,
      1
    ),
    size: options.size ?? size ?? product?.size ?? "",
    color: options.color ?? color ?? product?.color ?? "",
    price: numericPrice(product?.price),
  };
};

export const CartContext = createContext({
  cartItems: [],
  wishlistItems: [],
  cartCount: 0,
  wishlistCount: 0,
  cartSubtotal: 0,
  isCartOpen: false,
  notification: null,
});

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() =>
    readStoredArray(CART_STORAGE_KEY)
  );
  const [wishlistItems, setWishlistItems] = useState(() =>
    readStoredArray(WISHLIST_STORAGE_KEY)
  );
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [notification, setNotification] = useState(null);
  const notificationTimer = useRef(null);

  useEffect(() => {
    writeStoredArray(CART_STORAGE_KEY, cartItems);
  }, [cartItems]);

  useEffect(() => {
    writeStoredArray(WISHLIST_STORAGE_KEY, wishlistItems);
  }, [wishlistItems]);

  useEffect(
    () => () => {
      if (notificationTimer.current) {
        window.clearTimeout(notificationTimer.current);
      }
    },
    []
  );

  const dismissNotification = useCallback(() => {
    if (notificationTimer.current) {
      window.clearTimeout(notificationTimer.current);
      notificationTimer.current = null;
    }
    setNotification(null);
  }, []);

  const showNotification = useCallback((message, type = "success") => {
    if (notificationTimer.current) {
      window.clearTimeout(notificationTimer.current);
    }

    setNotification({ id: Date.now(), message, type });
    notificationTimer.current = window.setTimeout(() => {
      setNotification(null);
      notificationTimer.current = null;
    }, 3200);
  }, []);

  const openCart = useCallback(() => setIsCartOpen(true), []);
  const closeCart = useCallback(() => setIsCartOpen(false), []);
  const toggleCart = useCallback(() => setIsCartOpen((isOpen) => !isOpen), []);

  // Accepts the original `(id, quantity, size, color)` signature and the newer
  // `(product, { quantity, size, color })` form.
  const addToCart = useCallback(
    (productOrId, quantityOrOptions = 1, size = "", color = "") => {
      const line = normaliseCartInput(
        productOrId,
        quantityOrOptions,
        size,
        color
      );

      if (line.id === undefined || line.id === null) return;

      setCartItems((currentItems) => {
        const hasLine = currentItems.some((item) =>
          sameCartLine(item, line.id, line.size, line.color)
        );

        if (!hasLine) return [...currentItems, line];

        return currentItems.map((item) =>
          sameCartLine(item, line.id, line.size, line.color)
            ? { ...item, quantity: positiveInteger(item.quantity) + line.quantity }
            : item
        );
      });

      const product = getProduct(line.id);
      showNotification(`${product?.name || "Item"} added to your bag.`);
      openCart();
    },
    [openCart, showNotification]
  );

  const removeFromCart = useCallback(
    (productOrId, size = "", color = "") => {
      const id =
        productOrId && typeof productOrId === "object"
          ? productOrId.id
          : productOrId;
      const itemSize =
        productOrId && typeof productOrId === "object"
          ? productOrId.size || ""
          : size;
      const itemColor =
        productOrId && typeof productOrId === "object"
          ? productOrId.color || ""
          : color;

      setCartItems((currentItems) =>
        currentItems.filter(
          (item) => !sameCartLine(item, id, itemSize, itemColor)
        )
      );
      showNotification("Item removed from your bag.", "info");
    },
    [showNotification]
  );

  const updateQuantity = useCallback(
    (productOrId, size = "", color = "", quantity = 1) => {
      const isObject = productOrId && typeof productOrId === "object";
      const id = isObject ? productOrId.id : productOrId;
      const itemSize = isObject ? productOrId.size || "" : size;
      const itemColor = isObject ? productOrId.color || "" : color;
      const nextQuantity = isObject
        ? positiveInteger(
            typeof size === "number" ? size : productOrId.quantity
          )
        : positiveInteger(quantity);

      setCartItems((currentItems) =>
        currentItems.map((item) =>
          sameCartLine(item, id, itemSize, itemColor)
            ? { ...item, quantity: nextQuantity }
            : item
        )
      );
    },
    []
  );

  const clearCart = useCallback(() => {
    setCartItems([]);
    setIsCartOpen(false);
    showNotification("Your bag is now empty.", "info");
  }, [showNotification]);

  const addToWishlist = useCallback(
    (productOrId) => {
      const id =
        productOrId && typeof productOrId === "object"
          ? productOrId.id
          : productOrId;
      if (id === undefined || id === null) return;

      setWishlistItems((currentItems) =>
        currentItems.some((itemId) => sameId(itemId, id))
          ? currentItems
          : [...currentItems, id]
      );
      showNotification("Saved to your favourites.");
    },
    [showNotification]
  );

  const removeFromWishlist = useCallback(
    (productOrId) => {
      const id =
        productOrId && typeof productOrId === "object"
          ? productOrId.id
          : productOrId;
      setWishlistItems((currentItems) =>
        currentItems.filter((itemId) => !sameId(itemId, id))
      );
      showNotification("Removed from your favourites.", "info");
    },
    [showNotification]
  );

  const isWishlisted = useCallback(
    (productOrId) => {
      const id =
        productOrId && typeof productOrId === "object"
          ? productOrId.id
          : productOrId;
      return wishlistItems.some((itemId) => sameId(itemId, id));
    },
    [wishlistItems]
  );

  const toggleWishlist = useCallback(
    (productOrId) => {
      if (isWishlisted(productOrId)) {
        removeFromWishlist(productOrId);
      } else {
        addToWishlist(productOrId);
      }
    },
    [addToWishlist, isWishlisted, removeFromWishlist]
  );

  const cartCount = useMemo(
    () =>
      cartItems.reduce(
        (count, item) => count + positiveInteger(item.quantity),
        0
      ),
    [cartItems]
  );

  const cartSubtotal = useMemo(
    () =>
      cartItems.reduce((subtotal, item) => {
        const product = getProduct(item.id);
        const price = item.price || numericPrice(product?.price);
        return subtotal + price * positiveInteger(item.quantity);
      }, 0),
    [cartItems]
  );

  const value = useMemo(
    () => ({
      cartItems,
      setCartItems,
      addToCart,
      addCart: addToCart,
      removeFromCart,
      removeCart: removeFromCart,
      updateQuantity,
      clearCart,
      cartCount,
      cartSubtotal,
      wishlistItems,
      wishlist: wishlistItems,
      wishlistCount: wishlistItems.length,
      addToWishlist,
      addWishlist: addToWishlist,
      removeFromWishlist,
      removeWishlist: removeFromWishlist,
      toggleWishlist,
      isWishlisted,
      isCartOpen,
      miniCartOpen: isCartOpen,
      openCart,
      openMiniCart: openCart,
      closeCart,
      closeMiniCart: closeCart,
      toggleCart,
      notification,
      showNotification,
      dismissNotification,
    }),
    [
      addToCart,
      addToWishlist,
      cartCount,
      cartItems,
      cartSubtotal,
      clearCart,
      closeCart,
      dismissNotification,
      isCartOpen,
      isWishlisted,
      notification,
      openCart,
      removeFromCart,
      removeFromWishlist,
      showNotification,
      toggleCart,
      toggleWishlist,
      updateQuantity,
      wishlistItems,
    ]
  );

  return (
    <CartContext.Provider value={value}>
      {children}
      <div
        className={`store-toast${notification ? ` store-toast--visible store-toast--${notification.type}` : ""}`}
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        {notification && (
          <>
            <span className="store-toast__message">{notification.message}</span>
            <button
              className="store-toast__close"
              type="button"
              onClick={dismissNotification}
              aria-label="Dismiss notification"
            >
              ×
            </button>
          </>
        )}
      </div>
    </CartContext.Provider>
  );
};
